import * as THREE from 'three';
import { emis, std } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';
import { VENOM, legOf, taper, yjoint } from './weaver.ts';

/*
 * Arañita (docs/CRIATURAS.md §2, era III): what pours out of the Tejedora when she bursts. A quarter of her size, the
 * same ink and venom: a glossy cephalothorax with two pale green eyes, a round velvet abdomen with the beginning of
 * her pale mask on it (an oval and two hollow eyes, nothing more yet), stubby fangs and eight quick legs bent high
 * at the knee. Under 500 triangles; opaque shared materials, the venom eyes its one self-lit one, plus glow sprites.
 */

/** Uniform size of the miniature against its modelled coordinates. */
const SIZE = 0.42;

/** Each leg's hip (right side), yaw off the nose, knee and toe as (reach from the hip, height). */
const LEG: readonly { hip: THREE.Vector3; yaw: number; knee: [number, number]; ankle: [number, number]; toe: [number, number] }[] = [
  { hip: V(0.14, 0.46, 0.34), yaw: 0.5, knee: [0.42, 0.88], ankle: [0.82, 0.36], toe: [1.0, 0.0] },
  { hip: V(0.19, 0.45, 0.24), yaw: 1.12, knee: [0.38, 0.82], ankle: [0.72, 0.32], toe: [0.88, 0.0] },
  { hip: V(0.2, 0.45, 0.12), yaw: 1.9, knee: [0.36, 0.8], ankle: [0.7, 0.3], toe: [0.86, 0.0] },
  { hip: V(0.16, 0.45, 0.02), yaw: 2.5, knee: [0.42, 0.86], ankle: [0.8, 0.34], toe: [0.98, 0.0] },
];

/**
 * Bones: `body` (the cephalothorax) carries the `abdomen` (hung from the waist) and eight legs, each a `leg<i>` at
 * the hip and a `knee<i>`, turned so local +Z runs out along the leg (−X lifts, a Y turn swings it, +X on the knee
 * folds it in). Legs 0–3 are the right side front to back, 4–7 the left.
 */
export const spiderling = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.4, 67);
  const { g } = c;
  const ink = std('#0e0c13', { roughness: 0.26, metalness: 0.45, bumpScale: 0.006 });
  const velvet = std('#0a090d', { roughness: 0.72, metalness: 0.1, bumpScale: 0.03 });
  const bone = std('#c3c8ce', { roughness: 0.62, bumpScale: 0.015 });
  const fangM = std('#c4c6c4', { roughness: 0.35, metalness: 0.1 });
  const venom = emis('#d8ffb0', '#8cff3a', 1.7);

  const sc = G(g);
  sc.scale.setScalar(SIZE);
  const bodyJ = joint(sc, V(0, 0.5, 0.18), 'body');
  const bd = bodyJ.abs;
  const abdJ = joint(bd, V(0, 0.52, -0.06), 'abdomen');
  const ab = abdJ.abs;

  // Cephalothorax and head.
  const thx = M(new THREE.SphereGeometry(1, 7, 4), ink, bd);
  thx.position.set(0, 0.5, 0.2);
  thx.scale.set(0.24, 0.15, 0.3);
  // Eyes: one pale green pair, and the glow they cast.
  for (const k of [-1, 1]) {
    const e = M(new THREE.SphereGeometry(0.045, 4, 2), venom, bd);
    e.position.set(k * 0.06, 0.6, 0.44);
  }
  glowSprite(bd, VENOM, 0.3, V(0, 0.61, 0.46), 0.5);
  // Fangs: two stubby hooks under the head.
  for (const k of [-1, 1]) M(taper([V(k * 0.05, 0.46, 0.46), V(k * 0.055, 0.38, 0.5), V(k * 0.025, 0.33, 0.48)], 0.035, 0.01, 2, 4), fangM, bd);

  // Abdomen: round, riding a little high, the start of the mask on its back.
  const abd = M(new THREE.SphereGeometry(1, 8, 5), velvet, ab);
  abd.position.set(0, 0.62, -0.44);
  abd.scale.set(0.36, 0.31, 0.42);
  abd.rotation.x = -0.2;
  const mask = M(new THREE.SphereGeometry(1, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), bone, ab);
  mask.position.set(0, 0.89, -0.46);
  mask.scale.set(0.17, 0.06, 0.21);
  mask.rotation.x = -0.12;
  for (const k of [-1, 1]) {
    const eye = M(new THREE.SphereGeometry(0.035, 4, 2), velvet, ab);
    eye.position.set(k * 0.065, 0.945, -0.43);
    eye.scale.set(1.3, 0.5, 0.7);
  }

  // Legs: femur up to a high knee, then a single bent needle down to the ground.
  const legBones: Record<string, THREE.Object3D> = {};
  for (let i = 0; i < 8; i++) {
    const { side, row } = legOf(i);
    const L = LEG[row]!;
    const hip = V(side * L.hip.x, L.hip.y, L.hip.z);
    const yaw = side * L.yaw;
    const d = V(Math.sin(yaw), 0, Math.cos(yaw));
    const P = ([r, y]: [number, number]): THREE.Vector3 => hip.clone().addScaledVector(d, r).setY(y);
    const knee = P(L.knee), ankle = P(L.ankle), toe = P(L.toe);
    const th = yjoint(bd, hip, yaw, `leg${i}`);
    const sh = yjoint(th.abs, knee, yaw, `knee${i}`);
    legBones[`leg${i}`] = th.p;
    legBones[`knee${i}`] = sh.p;
    M(taper([hip, hip.clone().lerp(knee, 0.5).add(V(0, 0.04, 0)), knee], 0.045, 0.034, 2, 3, 1, 0.3), ink, th.abs);
    M(taper([knee, ankle, toe.clone().add(V(0, 0.02, 0))], 0.034, 0.012, 3, 3, 1, 2), ink, sh.abs);
  }

  return done(c, 0.94 * SIZE, undefined, undefined, bonesOf(c, { body: bodyJ.p, abdomen: abdJ.p, ...legBones }));
};
